import { inject, Injectable, signal, Signal } from '@angular/core';
import { IIqStatusLibraryStateService, Info, InfoSchema } from '../../models';
import {
    getProjectTitle,
    getTeam,
    getEnvironment,
    getProjectIcon,
    getDomain,
    getE2eProject,
    getLeveragesNonCritical,
    getComponetized,
    getStability,
    getAbstraction,
    getModularity,
    getTestCoverage,
    getUiUxCoverage,
    getE2eCoverage,
} from './iq-status-labels';
import { DatiqNxRepoClientService } from '@dat/datiq-nx-repo-client';
import * as am5percent from '@amcharts/amcharts5/percent';
import {
    ProjectInfo,
    ProjectQuery,
    ProjectSummary,
    ProjectUpdateInfo,
} from '@dat/datiq-nx-repo-utils';
import {
    ComponentViz,
    DepViz,
    EnvViz,
    LibraryViz,
    TeamViz,
} from './graph-builder';

@Injectable({
    providedIn: 'root',
})
export class IqStatusLibraryStateService
    implements IIqStatusLibraryStateService
{
    private _nxToolClient: DatiqNxRepoClientService = inject(
        DatiqNxRepoClientService,
    );

    private _depsSeries: am5percent.PieSeries;
    private _envSeries: am5percent.PieSeries;
    private _componentSeries: am5percent.PieSeries;
    private _librarySeries: am5percent.PieSeries;
    private _teamSeries: am5percent.PieSeries;
    private _loadedProjects: Map<string, ProjectInfo> = new Map<
        string,
        ProjectInfo
    >();
    constructor() {}

    async updateEnvViz(
        element: HTMLElement,
        query: ProjectQuery,
    ): Promise<void> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        if (!this._envSeries) {
            this._envSeries = EnvViz.build(element, results.env);
            return;
        }

        this._envSeries = EnvViz.update(this._envSeries, results.env);
    }
    async updateTeamViz(
        element: HTMLElement,
        query: ProjectQuery,
    ): Promise<void> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        if (!this._teamSeries) {
            this._teamSeries = TeamViz.build(element, results.teams);
            return;
        }

        this._teamSeries = TeamViz.update(this._teamSeries, results.teams);
    }
    async updateDomainViz(
        element: HTMLElement,
        query: ProjectQuery,
    ): Promise<void> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        if (!this._componentSeries) {
            this._componentSeries = ComponentViz.build(
                element,
                results.domains,
            );
            return;
        }

        this._componentSeries = ComponentViz.update(
            this._componentSeries,
            results.domains,
        );
    }
    async updateLibraryViz(
        element: HTMLElement,
        query: ProjectQuery,
    ): Promise<void> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        if (!this._librarySeries) {
            this._librarySeries = LibraryViz.build(
                element,
                results.libraryTypes,
            );
            return;
        }

        this._librarySeries = LibraryViz.update(
            this._librarySeries,
            results.libraryTypes,
        );
    }

    async updateDepViz(
        element: HTMLElement,
        query: ProjectQuery,
    ): Promise<void> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        if (!this._depsSeries) {
            this._depsSeries = DepViz.build(element, results.deps);
            return;
        }
        this._depsSeries = DepViz.update(this._depsSeries, results.deps);
    }
    async numOfProjects(query: ProjectQuery): Promise<number> {
        const results: ProjectSummary =
            await this._nxToolClient.getProjectSummary(query);

        return results.projects;
    }

    async queryProjects(query: ProjectQuery): Promise<ProjectInfo[]> {
        const projectInfo: ProjectInfo[] =
            await this._nxToolClient.queryProjects(query);

        return projectInfo;
    }

    async cardInfo(projectKey: string): Promise<Info> {
        const results: ProjectInfo = this._loadedProjects.has(projectKey)
            ? this._loadedProjects.get(projectKey)
            : await this._nxToolClient.getProject(projectKey);

        this._loadedProjects.set(projectKey, results);

        const info: Info = {
            name: getProjectTitle(results.proj),
            team: getTeam(results.team),
            environment: getEnvironment(
                results.prod
                    ? 'prod'
                    : results.staging
                    ? 'staging'
                    : results.nprod
                    ? 'nprod'
                    : results.notUsed
                    ? 'notUsed'
                    : 'Unknown',
            ),
            type: getProjectIcon(results.type),
            domain: getDomain(results.domain),
            e2eProject: getE2eProject(false),
            leveragesNonCritical: getLeveragesNonCritical(
                Object.values(results.deps)
                    .filter((dep: Partial<ProjectInfo>) => !dep.critical)
                    .map((dep: Partial<ProjectInfo>) => dep.proj),
            ),
            componetized: getComponetized(false),
            stability: getStability(0),
            abstraction: getAbstraction(0),
            modularity: getModularity(12),
            testCoverage: getTestCoverage(0.3),
            uiUxCoverage: getUiUxCoverage(0.4),
            e2eCoverage: getE2eCoverage(0.5),
            loaded: true,
        };

        return info;
    }

    async queryProjectKeys(query: ProjectQuery): Promise<string[]> {
        const keys: string[] = await this._nxToolClient.queryProjectKeys(query);

        return keys;
    }

    async updateProject(
        projectKey: string,
        projectInfo: Partial<ProjectUpdateInfo>,
    ) {
        const updatedProject: ProjectInfo =
            await this._nxToolClient.updateProject(projectKey, projectInfo);

        return updatedProject;
    }

    exportProjects(query: ProjectQuery) {
        return this._nxToolClient.exportProject('csv', query);
    }
}
